import { Vector3 } from "three";

/**
 * `vectorSlerp.ts` — the port of Unity's `Vector3.Slerp`, which three.js has
 * no equivalent of (`Quaternion.slerp` exists; `Vector3.slerp` does not).
 *
 * Unity's contract: the two arguments are treated as vectors FROM THE WORLD
 * ORIGIN; the DIRECTION is interpolated along the shortest great-circle arc
 * between them and the MAGNITUDE is interpolated linearly. So the result
 * sweeps an arc around the origin rather than cutting the chord a `lerp`
 * would take.
 *
 * It lives in its own module because it is the least self-evident part of the
 * scene-view camera move and the one thing there that is worth pinning: the
 * arc IS the visual difference between the desktop client's camera transition
 * and a naive straight-line one, and the degenerate cases below are silent
 * NaN sources rather than loud failures.
 *
 * Used by `OrbitCameraController.transitionToPose`, the port of
 * `DesktopSceneModel.CoTransitionCamera` (`DesktopSceneModel.cs:531`), which
 * slerps the camera position and the orbit pivot on a linear clock.
 */

/** Below this a vector has no usable direction to interpolate. */
const K_MIN_LENGTH = 1e-6;
/** Below this an angle is degenerate: parallel, or exactly antiparallel. */
const K_MIN_ANGLE = 1e-4;

/**
 * Spherically interpolate `from` → `to` at `t`, writing into `out` (returned).
 *
 * Degenerate inputs fall back to a plain lerp, which is what the spherical
 * form converges to anyway: a zero-length endpoint has no direction, and an
 * exactly-antiparallel pair has no *shortest* arc — every plane through the
 * origin is equally "between" them, so `sin(angle)` is 0 and the spherical
 * formula divides by zero. Without the guard a camera pose that happens to sit
 * at the world origin, or a pivot that passes exactly through it, produces
 * NaNs and freezes the view.
 */
export function slerpVectors(
  from: Vector3,
  to: Vector3,
  t: number,
  out: Vector3,
): Vector3 {
  const fromLen = from.length();
  const toLen = to.length();

  if (fromLen < K_MIN_LENGTH || toLen < K_MIN_LENGTH) {
    return out.lerpVectors(from, to, t);
  }

  const dot = clamp(from.dot(to) / (fromLen * toLen), -1, 1);
  const angle = Math.acos(dot);
  if (angle < K_MIN_ANGLE || Math.PI - angle < K_MIN_ANGLE) {
    return out.lerpVectors(from, to, t);
  }

  const sinAngle = Math.sin(angle);
  const a = Math.sin((1 - t) * angle) / sinAngle;
  const b = Math.sin(t * angle) / sinAngle;
  out.set(
    (from.x / fromLen) * a + (to.x / toLen) * b,
    (from.y / fromLen) * a + (to.y / toLen) * b,
    (from.z / fromLen) * a + (to.z / toLen) * b,
  );
  // The blended direction is unit-length only when both inputs are; scale it
  // to the interpolated magnitude explicitly rather than trusting that.
  return out.normalize().multiplyScalar(fromLen + (toLen - fromLen) * t);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
